import React, { useState, useEffect } from 'react';
import api from '../config/axios';
import * as XLSX from 'xlsx';

interface SellerData {
  email: string;
  name: string;
  data: {
    completed: { ventas: number; suma_rb: number; suma_cf: number };
    scheduled: { ventas: number; suma_rb: number; suma_cf: number };
    total: { ventas: number; suma_rb: number; suma_cf: number };
  };
}

interface SupervisorData {
  name: string;
  supervisor_name: string;
  sellers: { [key: string]: SellerData };
  totals: {
    completed: { ventas: number; suma_rb: number; suma_cf: number };
    scheduled: { ventas: number; suma_rb: number; suma_cf: number };
    total: { ventas: number; suma_rb: number; suma_cf: number };
  };
}

interface SegmentData {
  name: string;
  supervisors: { [key: string]: SupervisorData };
}

interface ReportData {
  segments: SegmentData[];
  generalTotals: {
    completed: { ventas: number; suma_rb: number; suma_cf: number };
    scheduled: { ventas: number; suma_rb: number; suma_cf: number };
    total: { ventas: number; suma_rb: number; suma_cf: number };
  };
}

const AdvanceGeneral: React.FC = () => {
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSegment, setSelectedSegment] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  useEffect(() => {
    // Fechas por defecto (mes actual)
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    
    setFromDate(startDate);
    setToDate(endDate);
    loadData(startDate, endDate, '');
  }, []);

  const loadData = async (from: string, to: string, segment: string) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      if (segment) params.append('segment', segment);

      const response = await api.get(`/reports/advance-general?${params}`);
      setData(response.data);
    } catch (error) {
      console.error('Error cargando datos:', error);
      alert('Error al cargar los datos del reporte');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    loadData(fromDate, toDate, selectedSegment);
  };

  const safeText = (v: any, fallback: string = '') => (v === null || v === undefined ? fallback : String(v));

  const exportToExcel = () => {
    if (!data) return;

    const workbook = XLSX.utils.book_new();
    
    data.segments.forEach(segment => {
      const worksheetData: any[] = [];
      
      // Encabezados
      worksheetData.push([
        'Etiquetas de fila',
        'ESTADO completed',
        '',
        '',
        'Scheduled',
        '',
        '',
        'Total',
        '',
        ''
      ]);
      worksheetData.push([
        '',
        'VENTAS',
        'Suma de RB',
        'Suma de CF',
        'VENTAS',
        'Suma de RB',
        'Suma de CF',
        'VENTAS',
        'Suma de RB',
        'Suma de CF'
      ]);

      Object.values(segment.supervisors).forEach(supervisor => {
        // Fila del supervisor (completed)
        const supLabel = safeText(supervisor.supervisor_name || supervisor.name, 'SIN SUPERVISOR');
        worksheetData.push([
          `SUPERVISOR ${supLabel.toUpperCase()}`,
          supervisor.totals.completed.ventas,
          supervisor.totals.completed.suma_rb.toFixed(2),
          supervisor.totals.completed.suma_cf.toFixed(2),
          supervisor.totals.scheduled.ventas,
          supervisor.totals.scheduled.suma_rb.toFixed(2),
          supervisor.totals.scheduled.suma_cf.toFixed(2),
          supervisor.totals.total.ventas,
          supervisor.totals.total.suma_rb.toFixed(2),
          supervisor.totals.total.suma_cf.toFixed(2)
        ]);

        // Filas de vendedores
        Object.values(supervisor.sellers).forEach(seller => {
          worksheetData.push([
            safeText(seller.name || seller.email, 'SIN NOMBRE'),
            seller.data.completed.ventas,
            seller.data.completed.suma_rb.toFixed(2),
            seller.data.completed.suma_cf.toFixed(2),
            seller.data.scheduled.ventas,
            seller.data.scheduled.suma_rb.toFixed(2),
            seller.data.scheduled.suma_cf.toFixed(2),
            seller.data.total.ventas,
            seller.data.total.suma_rb.toFixed(2),
            seller.data.total.suma_cf.toFixed(2)
          ]);
        });
      });

      // Total general
      worksheetData.push([
        'Total general',
        data.generalTotals.completed.ventas,
        data.generalTotals.completed.suma_rb.toFixed(2),
        data.generalTotals.completed.suma_cf.toFixed(2),
        data.generalTotals.scheduled.ventas,
        data.generalTotals.scheduled.suma_rb.toFixed(2),
        data.generalTotals.scheduled.suma_cf.toFixed(2),
        data.generalTotals.total.ventas,
        data.generalTotals.total.suma_rb.toFixed(2),
        data.generalTotals.total.suma_cf.toFixed(2)
      ]);

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, `AVANCE GENERAL ${segment.name}`);
    });

    XLSX.writeFile(workbook, `Avance_General_${fromDate}_${toDate}.xlsx`);
  };

  const renderSegment = (segment: SegmentData) => (
    <div key={segment.name} className="mb-4">
      <h3 className="h4 text-primary mb-3">AVANCE GENERAL {segment.name.toUpperCase()}</h3>
      
      <div className="table-responsive">
        <table className="table table-bordered table-striped">
          <thead className="table-dark">
            <tr>
              <th rowSpan={2} className="text-center align-middle">Etiquetas de fila</th>
              <th colSpan={3} className="text-center">ESTADO completed</th>
              <th colSpan={3} className="text-center">Scheduled</th>
              <th colSpan={3} className="text-center">Total</th>
            </tr>
            <tr>
              <th className="text-center">VENTAS</th>
              <th className="text-center">Suma de RB</th>
              <th className="text-center">Suma de CF</th>
              <th className="text-center">VENTAS</th>
              <th className="text-center">Suma de RB</th>
              <th className="text-center">Suma de CF</th>
              <th className="text-center">VENTAS</th>
              <th className="text-center">Suma de RB</th>
              <th className="text-center">Suma de CF</th>
            </tr>
          </thead>
          <tbody>
            {Object.values(segment.supervisors).map(supervisor => (
              <React.Fragment key={supervisor.name}>
                {/* Fila del supervisor */}
                <tr className="table-info">
                  <td><strong>SUPERVISOR {(supervisor.supervisor_name || supervisor.name || 'Sin supervisor').toUpperCase()}</strong></td>
                  <td className="text-center">{supervisor.totals.completed.ventas}</td>
                  <td className="text-end">{supervisor.totals.completed.suma_rb.toFixed(2)}</td>
                  <td className="text-end">{supervisor.totals.completed.suma_cf.toFixed(2)}</td>
                  <td className="text-center">{supervisor.totals.scheduled.ventas}</td>
                  <td className="text-end">{supervisor.totals.scheduled.suma_rb.toFixed(2)}</td>
                  <td className="text-end">{supervisor.totals.scheduled.suma_cf.toFixed(2)}</td>
                  <td className="text-center"><strong>{supervisor.totals.total.ventas}</strong></td>
                  <td className="text-end"><strong>{supervisor.totals.total.suma_rb.toFixed(2)}</strong></td>
                  <td className="text-end"><strong>{supervisor.totals.total.suma_cf.toFixed(2)}</strong></td>
                </tr>
                
                {/* Filas de vendedores */}
                {Object.values(supervisor.sellers).map(seller => (
                  <tr key={seller.email} className="table-light">
                    <td className="ps-4">{seller.name || seller.email || 'Sin nombre'}</td>
                    <td className="text-center">{seller.data.completed.ventas}</td>
                    <td className="text-end">{seller.data.completed.suma_rb.toFixed(2)}</td>
                    <td className="text-end">{seller.data.completed.suma_cf.toFixed(2)}</td>
                    <td className="text-center">{seller.data.scheduled.ventas}</td>
                    <td className="text-end">{seller.data.scheduled.suma_rb.toFixed(2)}</td>
                    <td className="text-end">{seller.data.scheduled.suma_cf.toFixed(2)}</td>
                    <td className="text-center">{seller.data.total.ventas}</td>
                    <td className="text-end">{seller.data.total.suma_rb.toFixed(2)}</td>
                    <td className="text-end">{seller.data.total.suma_cf.toFixed(2)}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            
            {/* Total general */}
            <tr className="table-warning">
              <td><strong>Total general</strong></td>
              <td className="text-center"><strong>{data?.generalTotals.completed.ventas}</strong></td>
              <td className="text-end"><strong>{data?.generalTotals.completed.suma_rb.toFixed(2)}</strong></td>
              <td className="text-end"><strong>{data?.generalTotals.completed.suma_cf.toFixed(2)}</strong></td>
              <td className="text-center"><strong>{data?.generalTotals.scheduled.ventas}</strong></td>
              <td className="text-end"><strong>{data?.generalTotals.scheduled.suma_rb.toFixed(2)}</strong></td>
              <td className="text-end"><strong>{data?.generalTotals.scheduled.suma_cf.toFixed(2)}</strong></td>
              <td className="text-center"><strong>{data?.generalTotals.total.ventas}</strong></td>
              <td className="text-end"><strong>{data?.generalTotals.total.suma_rb.toFixed(2)}</strong></td>
              <td className="text-end"><strong>{data?.generalTotals.total.suma_cf.toFixed(2)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Reporte Avance General</h2>
            <button 
              className="btn btn-success"
              onClick={exportToExcel}
              disabled={!data}
            >
              📊 Exportar a Excel
            </button>
          </div>

          {/* Filtros */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label">Desde:</label>
                  <input
                    type="date"
                    className="form-control"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Hasta:</label>
                  <input
                    type="date"
                    className="form-control"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Segmento:</label>
                  <select
                    className="form-select"
                    value={selectedSegment}
                    onChange={(e) => setSelectedSegment(e.target.value)}
                  >
                    <option value="">Todos los segmentos</option>
                    <option value="B2B">B2B</option>
                    <option value="B2C">B2C</option>
                  </select>
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <button 
                    className="btn btn-primary w-100"
                    onClick={handleFilter}
                  >
                    🔍 Filtrar
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Reporte */}
          {data && data.segments.length > 0 ? (
            data.segments.map(renderSegment)
          ) : (
            <div className="alert alert-info">
              <h4>No hay datos para mostrar</h4>
              <p>No se encontraron ventas para el período y filtros seleccionados.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdvanceGeneral;
